import React from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  CloseIcon,
  Box,
  FormControlLabel,
  Checkbox,
} from "app/material-framework/materialComponents";
import { LoadingSpinner } from "lib/Spinner";
import { DataErrorMsg } from "../../pages/AnalystDirectory/AnalystDirectory";

export const MaterialDialog = ({
  open,
  onReject,
  onAccept,
  onClose,
  isLoading,
  isError,
  title,
  body,
  showDoNotShowAgain,
  doNotShowAgain,
  handleCheckboxChange,
  cancelBtnText = "Cancel",
  acceptBtnText = "Accept",
  closeIcon = true,
  showDialogActions = true,
  "data-testid": testId = "material",
}) => {
  const renderContent = () => {
    if (isLoading) {
      return <LoadingSpinner className="mx-auto pt-5 pb-5" />;
    }

    if (isError) {
      return <DataErrorMsg />;
    }

    return (
      <DialogContentText
        id="alert-dialog-description"
        data-testid={`${testId}-dialog-content-text`}
        sx={{
          pt: 3,
          color: "rgba(0, 0, 0, 0.8)",
        }}
      >
        {body}
      </DialogContentText>
    );
  };

  return (
    <Dialog
      open={open}
      maxWidth="md"
      onClose={onClose}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      data-testid={`${testId}-dialog`}
      PaperProps={{
        sx: {
          borderRadius: "12px",
          boxShadow: "0 8px 32px rgba(0, 0, 0, 0.12)",
        },
      }}
      BackdropProps={{
        sx: {
          backgroundColor: "rgba(0, 0, 0, 0.8)",
        },
      }}
    >
      <DialogTitle
        id="alert-dialog-title"
        data-testid={`${testId}-dialog-title`}
        sx={{
          fontSize: "1.25rem",
          color: "#CA2420",
          fontWeight: 600,
          borderBottom: "1px solid #e0e0e0",
          pb: 2,
        }}
      >
        {title}
        {closeIcon && (
          <IconButton
            aria-label="close"
            onClick={onReject}
            data-testid={`${testId}-dialog-close-icon-button`}
            sx={{
              position: "absolute",
              right: 16,
              top: 16,
              backgroundColor: "transparent",
              border: "1.5px solid",
              borderColor: "grey.300",
              width: 32,
              height: 32,
              "&:hover": {
                backgroundColor: "grey.100",
                borderColor: "grey.400",
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        )}
      </DialogTitle>
      <DialogContent>{renderContent()}</DialogContent>
      {showDialogActions && (
        <DialogActions
          sx={{
            padding: "16px 24px",
            gap: "12px",
            borderTop: "1px solid #e0e0e0",
          }}
        >
          {showDoNotShowAgain && (
            <FormControlLabel
              data-testid={`${testId}-dialog-do-not-show-again`}
              control={
                <Checkbox
                  checked={doNotShowAgain}
                  onChange={handleCheckboxChange}
                  disabled={isLoading}
                  size="small"
                  sx={{
                    padding: "4px",
                    "&:hover": {
                      backgroundColor: "rgba(0, 0, 0, 0.1)",
                    },
                  }}
                />
              }
              label="Do not show again"
              sx={{
                margin: 0,
                "& .MuiFormControlLabel-label": {
                  fontSize: "0.875rem",
                  cursor: "pointer",
                },
              }}
            />
          )}

          <Box sx={{ flexGrow: 1 }} />

          <Box sx={{ display: "flex", gap: "12px" }}>
            <Button
              onClick={onReject}
              variant="outlined"
              color="warning"
              size="large"
              data-testid={`${testId}-dialog-cancel-button`}
              sx={{
                minWidth: "120px",
                textTransform: "none",
                borderRadius: "8px",
                fontWeight: 500,
              }}
              disabled={isLoading}
            >
              {cancelBtnText}
            </Button>
            <Button
              onClick={onAccept}
              variant="contained"
              color="error"
              size="large"
              data-testid={`${testId}-dialog-accept-button`}
              autoFocus
              sx={{
                minWidth: "120px",
                textTransform: "none",
                borderRadius: "8px",
                fontWeight: 500,
                boxShadow: "0 4px 12px rgba(211, 47, 47, 0.3)",
                "&:hover": {
                  boxShadow: "0 6px 16px rgba(211, 47, 47, 0.6)",
                },
              }}
              disabled={isLoading || isError}
            >
              {acceptBtnText}
            </Button>
          </Box>
        </DialogActions>
      )}
    </Dialog>
  );
};
